import type { GeneralParameters } from '../../general/parameters/general'
import type { SessionEndParameters } from '../parameters/session-end'

import { Analytix } from '~/shared/analytix'
import { AnalytixEvent } from '../../general/event'
import { Gate, seeds } from '../../general/gate'
import { parameters } from '../parameters/session-end'

export class SessionEndEvent extends AnalytixEvent<SessionEndParameters> {
  name = 'session_end'
  group = null
  parameters = parameters
  sender = Analytix.prototype.sendSessionEndEvent as any

  // `session_end` closes only session gate.
  // Player/play gates are closed by `player_close` synthetic event on dispose.
  gates = [Gate.Closes(seeds.session)]

  constructor() {
    super()

    // `session_end` readiness depends on having a previous distinct session snapshot.
    // We subscribe to general parameter updates and re-verify until it becomes valid.
    this.verify().then((ready) => {
      if (!ready) {
        const off = this.general.updates.on(() => this.verify())
        this.readiness.finally(off)
      }
    })
  }

  /**
   * Validate required parameters for `session_end` event.
   */
  validate(): false | [GeneralParameters, SessionEndParameters] {
    const now = Date.now()
    const sessionId = this.general.get('sessionId')
    const previous = this.general.latest((v, ts, stop) => {
      if (now - ts > this.timeout) return stop // stop searching if snapshot is too old
      const prevSessionId = v.get('sessionId')
      return prevSessionId != null && prevSessionId !== sessionId
    })

    // valid conditions for `session_end` event:
    // - there was a previous snapshot with a different `sessionId` within the timeout period,
    //   which indicates that a new session has started and the previous one has ended
    const valid = previous != null && now - previous.ts < this.timeout

    return (
      valid && [
        Object.fromEntries(previous.map),
        Object.fromEntries(this.parameters),
      ]
    )
  }
}
